import { type ReactNode } from 'react'
import { View, Text } from 'react-native'
import { CreditCard, Wallet } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, INTERACTION, withAlpha } from '@/lib/theme/tokens'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import type { CheckoutPaymentMethod } from '@/lib/payments/processor-fee'

export function CheckoutMethodPicker({
  method,
  onChange,
  cardLabel,
  paypalLabel,
  title,
}: {
  method: CheckoutPaymentMethod
  onChange: (method: CheckoutPaymentMethod) => void
  cardLabel: string
  paypalLabel: string
  title: string
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View style={{ gap: SPACING.sm }} testID="checkout.payment-method-selector">
      <Text
        style={{
          fontSize: 17,
          lineHeight: 22,
          fontFamily: FONTS.bodyBold,
          color: colors.textPrimary,
        }}
      >
        {title}
      </Text>
      <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
        <MethodChip
          selected={method === 'card'}
          label={cardLabel}
          testID="checkout.payment-method-card"
          onPress={() => onChange('card')}
          icon={
            <CreditCard
              size={18}
              color={method === 'card' ? COLORS.coral.DEFAULT : colors.textSecondary}
              strokeWidth={1.8}
            />
          }
        />
        <MethodChip
          selected={method === 'paypal'}
          label={paypalLabel}
          testID="checkout.payment-method-paypal"
          onPress={() => onChange('paypal')}
          icon={
            <Wallet
              size={18}
              color={method === 'paypal' ? COLORS.coral.DEFAULT : colors.textSecondary}
              strokeWidth={1.8}
            />
          }
        />
      </View>
    </View>
  )
}

function MethodChip({
  selected,
  label,
  testID,
  onPress,
  icon,
}: {
  selected: boolean
  label: string
  testID: string
  onPress: () => void
  icon: ReactNode
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      testID={testID}
      style={{
        flex: 1,
        minHeight: INTERACTION.fieldHeight,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: SPACING.xs,
        paddingHorizontal: SPACING.sm,
        borderRadius: RADIUS.md,
        borderWidth: 1,
        borderColor: selected ? withAlpha(COLORS.coral.DEFAULT, 0.4) : colors.border,
        backgroundColor: selected ? withAlpha(COLORS.coral.DEFAULT, 0.05) : colors.surface,
      }}
    >
      {icon}
      <Text
        style={{
          fontSize: 14,
          fontFamily: FONTS.body,
          color: colors.textPrimary,
        }}
      >
        {label}
      </Text>
    </Pressable>
  )
}
